import { v4 as uuid4 } from "uuid"
import {
  DecisionReason,
  Experiment,
  HackleEvent,
  HackleUser,
  IdentifierType,
  Long,
  Properties,
  RemoteConfigParameter,
  VariationId,
  VariationKey
} from "../model/model"
import { EventDto, ExposureEventDto, RemoteConfigEventDto, TrackEventDto } from "./dto"
import PropertyUtil from "../util/PropertyUtil"
import ExperimentEvaluation from "../evaluation/service/experiment/ExperimentEvaluation"
import RemoteConfigEvaluation from "../evaluation/service/remoteconfig/RemoteConfigEvaluation"
import Workspace from "../workspace/Workspace"

export default abstract class UserEvent {
  timestamp: Long
  user: HackleUser
  public readonly insertId: string
  public readonly internalProperties: Properties

  protected constructor(
    timestamp: Long,
    user: HackleUser,
    internalProperties: Properties,
    insertId: string = uuid4()
  ) {
    this.timestamp = timestamp
    this.user = user
    this.internalProperties = internalProperties
    this.insertId = insertId
  }

  static exposure(
    timestamp: number,
    user: HackleUser,
    workspace: Workspace,
    evaluation: ExperimentEvaluation,
    properties: Properties
  ): ExposureEvent {
    return new ExposureEvent(
      timestamp,
      user,
      evaluation.entity,
      evaluation.result.variation.id,
      evaluation.result.variation.key,
      evaluation.result.reason,
      properties,
      workspace.toProperties()
    )
  }

  static track(timestamp: number, user: HackleUser, workspace: Workspace | null, event: HackleEvent): TrackEvent {
    // Sanitize at creation so every downstream consumer (e.g. event trigger matching)
    // sees the same properties that are sent to the server.
    const sanitizedEvent: HackleEvent = { ...event, properties: PropertyUtil.sanitize(event.properties) }
    return new TrackEvent(timestamp, user, sanitizedEvent, workspace?.toProperties() ?? {})
  }

  static remoteConfig(
    timestamp: number,
    user: HackleUser,
    workspace: Workspace,
    evaluation: RemoteConfigEvaluation,
    properties: Properties
  ): RemoteConfigEvent {
    return new RemoteConfigEvent(
      timestamp,
      user,
      evaluation.entity,
      evaluation.result.value?.id,
      evaluation.result.reason,
      properties,
      workspace.toProperties()
    )
  }

  static isExposureEvent(event: UserEvent): event is ExposureEvent {
    return (event as ExposureEvent).experiment !== undefined
  }

  static isTrackEvent(event: UserEvent): event is TrackEvent {
    return (event as TrackEvent).event !== undefined
  }

  static isRemoteConfigEvent(event: UserEvent): event is RemoteConfigEvent {
    return (event as RemoteConfigEvent).parameter !== undefined
  }

  static isExposureEventDto(event: EventDto): event is ExposureEventDto {
    return (
      "experimentId" in (event as ExposureEventDto) && typeof (event as ExposureEventDto).experimentId !== "undefined"
    )
  }

  static isTrackEventDto(event: EventDto): event is TrackEventDto {
    return "eventTypeId" in (event as TrackEventDto) && typeof (event as TrackEventDto).eventTypeId !== "undefined"
  }

  static isRemoteConfigEventDto(event: EventDto): event is RemoteConfigEventDto {
    return (
      "parameterId" in (event as RemoteConfigEventDto) &&
      typeof (event as RemoteConfigEventDto).parameterId !== "undefined"
    )
  }

  abstract copyWithUser(user: HackleUser): UserEvent

  toDto(): EventDto {
    return {
      insertId: this.insertId,
      timestamp: this.timestamp,

      userId: this.user.identifiers[IdentifierType.ID],
      identifiers: this.user.identifiers,
      userProperties: PropertyUtil.sanitize(this.user.properties),
      hackleProperties: PropertyUtil.sanitize(this.user.hackleProperties),
      internalProperties: this.internalProperties
    }
  }
}

export class ExposureEvent extends UserEvent {
  experiment: Experiment
  variationId?: VariationId
  variationKey: VariationKey
  decisionReason: DecisionReason
  properties: Properties

  constructor(
    timestamp: Long,
    user: HackleUser,
    experiment: Experiment,
    variationId: VariationId | undefined,
    variationKey: VariationKey,
    decisionReason: DecisionReason,
    properties: Properties,
    internalProperties: Properties = {},
    insertId?: string
  ) {
    super(timestamp, user, internalProperties, insertId)
    this.experiment = experiment
    this.variationId = variationId
    this.variationKey = variationKey
    this.decisionReason = decisionReason
    this.properties = properties
  }

  copyWithUser(user: HackleUser): ExposureEvent {
    return new ExposureEvent(
      this.timestamp,
      user,
      this.experiment,
      this.variationId,
      this.variationKey,
      this.decisionReason,
      this.properties,
      this.internalProperties,
      this.insertId
    )
  }

  toDto(): ExposureEventDto {
    return {
      ...super.toDto(),

      experimentId: this.experiment.id,
      experimentKey: this.experiment.key,
      experimentType: this.experiment.type,
      experimentVersion: this.experiment.version,
      variationId: this.variationId,
      variationKey: this.variationKey,
      decisionReason: this.decisionReason.toString(),
      properties: this.properties
    }
  }
}

export class TrackEvent extends UserEvent {
  event: HackleEvent

  constructor(
    timestamp: Long,
    user: HackleUser,
    event: HackleEvent,
    internalProperties: Properties = {},
    insertId?: string
  ) {
    super(timestamp, user, internalProperties, insertId)
    this.event = event
  }

  copyWithUser(user: HackleUser): TrackEvent {
    return new TrackEvent(this.timestamp, user, this.event, this.internalProperties, this.insertId)
  }

  toDto(): TrackEventDto {
    return {
      ...super.toDto(),

      // The event type id is resolved server-side from the key; the client sends 0.
      eventTypeId: 0,
      eventTypeKey: this.event.key,
      value: this.event.value || 0,
      properties: PropertyUtil.sanitize(this.event.properties)
    }
  }
}

export class RemoteConfigEvent extends UserEvent {
  parameter: RemoteConfigParameter
  valueId: number | undefined
  decisionReason: DecisionReason
  properties: Properties

  constructor(
    timestamp: Long,
    user: HackleUser,
    parameter: RemoteConfigParameter,
    valueId: number | undefined,
    decisionReason: DecisionReason,
    properties: Properties,
    internalProperties: Properties = {},
    insertId?: string
  ) {
    super(timestamp, user, internalProperties, insertId)
    this.parameter = parameter
    this.valueId = valueId
    this.decisionReason = decisionReason
    this.properties = properties
  }

  copyWithUser(user: HackleUser): RemoteConfigEvent {
    return new RemoteConfigEvent(
      this.timestamp,
      user,
      this.parameter,
      this.valueId,
      this.decisionReason,
      this.properties,
      this.internalProperties,
      this.insertId
    )
  }

  toDto(): RemoteConfigEventDto {
    return {
      ...super.toDto(),
      parameterId: this.parameter.id,
      parameterKey: this.parameter.key,
      parameterType: this.parameter.type,
      valueId: this.valueId,
      decisionReason: this.decisionReason.toString(),
      properties: PropertyUtil.sanitize(this.properties)
    }
  }
}
